import React, { useEffect, useState } from "react";
import callApi from "../../helpers/networkHelper";
import API_CONFIG from "../../config/apiConstant";
import DataTable from "../common/DataTable";
import EmptyState from "../common/EmptyState";
import ChipList from "../common/ChipList";

const TRINETRA_PAGE_SIZE = 5;

const TrinetraDashboardSection = ({ onError }) => {
  const [summary, setSummary] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSummary();
  }, []);

  useEffect(() => {
    fetchLicenses();
    // eslint-disable-next-line
  }, [page]);

  const fetchSummary = async () => {
    try {
      const res = await callApi(
        API_CONFIG.DASHBOARD.TRINETRA_SUMMARY.METHOD,
        API_CONFIG.DASHBOARD.TRINETRA_SUMMARY.ENDPOINT
      );
      setSummary(res.data.data);
    } catch {
      onError("Failed to load Trinetra summary");
    } finally {
      setLoading(false);
    }
  };

  const fetchLicenses = async () => {
    try {
      const res = await callApi(
        API_CONFIG.TRINETRA_LICENSE.LIST.METHOD,
        `${API_CONFIG.TRINETRA_LICENSE.LIST.ENDPOINT}?page=${page}&limit=${TRINETRA_PAGE_SIZE}`
      );
      setLicenses(res.data.data || []);
      setTotalPages(Math.ceil((res.data.total || 0) / TRINETRA_PAGE_SIZE));
    } catch {
      onError("Failed to load Trinetra licenses");
    }
  };

  const getStatus = (license) => {
    if (license.expiresAt && new Date() > new Date(license.expiresAt)) return "EXPIRED";
    return "ACTIVE";
  };

  const licenseColumns = [
    {
      key: "client",
      label: "Client",
      render: (l) =>
        l.clientObjectId?.clientName || l.clientId?.clientName || "—",
    },
    {
      key: "licenseId",
      label: "License ID",
      nowrap: true,
      render: (l) => (
        <span className="font-medium" style={{ color: "var(--brand)" }}>
          {l.licenseId}
        </span>
      ),
    },
    {
      key: "description",
      label: "Description",
      // Truncated, full text on hover — see the note in trinetraProductHandler.
      render: (l) =>
        l.description ? (
          <span className="block max-w-[200px] truncate" title={l.description}>
            {l.description}
          </span>
        ) : (
          <span style={{ color: "var(--main-text-muted)" }}>—</span>
        ),
    },
    {
      key: "licenseType",
      label: "Type",
      nowrap: true,
      render: (l) => {
        const isPerpetual = l.licenseType === "PERPETUAL";
        return (
          <span
            className="inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold border"
            style={{
              background: isPerpetual
                ? "var(--license-perpetual-bg)"
                : "var(--license-trial-bg)",
              color: isPerpetual
                ? "var(--license-perpetual-text)"
                : "var(--license-trial-text)",
              borderColor: isPerpetual
                ? "var(--license-perpetual-border)"
                : "var(--license-trial-border)",
            }}
          >
            {l.licenseType}
          </span>
        );
      },
    },
    {
      key: "maxCameras",
      label: "Cameras",
      align: "right",
      nowrap: true,
      render: (l) => (
        <span className="font-semibold" style={{ color: "var(--brand)" }}>
          {l.maxCameras}
        </span>
      ),
    },
    { key: "maxAiModels", label: "AI Models", align: "right", nowrap: true },
    { key: "maxNodes", label: "Nodes", align: "right", nowrap: true },
    {
      key: "allowedModels",
      label: "Allowed Models",
      render: (l) => (
        <ChipList
          items={l.allowedModels || []}
          muted={new Set(l.customModels || [])}
          mutedTitle="Custom model"
          max={3}
          palette={{
            bg: "var(--chip-model-bg)",
            text: "var(--chip-model-text)",
            border: "var(--chip-model-border)",
          }}
        />
      ),
    },
    {
      key: "features",
      label: "Features",
      render: (l) => (
        <ChipList
          items={l.trinetraFeatures || []}
          muted={new Set(l.customFeatures || [])}
          mutedTitle="Custom feature"
          max={4}
          palette={{
            bg: "var(--chip-feature-bg)",
            text: "var(--chip-feature-text)",
            border: "var(--chip-feature-border)",
          }}
        />
      ),
    },
    {
      key: "status",
      label: "Status",
      nowrap: true,
      render: (l) => {
        const status = getStatus(l);
        return (
          <span
            className="px-3 py-1 rounded-full text-xs font-semibold border"
            style={{
              color: status === "ACTIVE" ? "var(--success)" : "var(--danger)",
              borderColor:
                status === "ACTIVE" ? "var(--success-border)" : "var(--danger-border)",
              background: status === "ACTIVE" ? "var(--success-bg)" : "var(--danger-bg)",
            }}
          >
            {status}
          </span>
        );
      },
    },
    {
      key: "expiresAt",
      label: "Expiry",
      nowrap: true,
      render: (l) => (
        <span style={{ color: "var(--main-text-muted)" }}>
          {l.expiresAt ? new Date(l.expiresAt).toLocaleDateString("en-GB") : "Never"}
        </span>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="mt-10 rounded-lg border p-6" style={{ background: "var(--card-bg)", borderColor: "var(--card-border)" }}>
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-48 rounded" style={{ background: "var(--card-border)" }} />
          <div className="grid grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-20 rounded" style={{ background: "var(--card-border)" }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-10">
      {/* TRINETRA HEADER */}
      <h2 className="text-xl font-semibold mb-4" style={{ color: "var(--main-text)" }}>Trinetra License Summary</h2>

      {/* TRINETRA STAT CARDS */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <TrinetraStatCard
            title="Trinetra Licenses"
            items={[
              { label: "Total", value: summary.total, color: "text-blue-400" },
              { label: "Active", value: summary.active, color: "text-green-400" },
              { label: "Expired", value: summary.expired, color: "text-red-400" },
            ]}
          />
          <TrinetraStatCard
            title="Trinetra Clients"
            items={[
              { label: "Total", value: summary.totalClients, color: "text-blue-400" },
              { label: "Active", value: summary.activeClients, color: "text-green-400" },
            ]}
          />
          <TrinetraStatCard
            title="License Types"
            items={[
              { label: "Trial", value: summary.trialCount, color: "text-yellow-400" },
              { label: "Perpetual", value: summary.perpetualCount, color: "text-purple-400" },
            ]}
          />
          <TrinetraStatCard
            title="Capacity"
            items={[
              { label: "Total Cameras", value: summary.totalCameras, color: "text-cyan-400" },
              { label: "Total Nodes", value: summary.totalNodes, color: "text-orange-400" },
            ]}
          />
        </div>
      )}

      {/* TRINETRA LICENSE TABLE */}
      {/* TRINETRA LICENSE TABLE */}
      <div className="mb-3">
        <h3 className="text-lg font-semibold" style={{ color: "var(--main-text)" }}>Recent Trinetra Licenses</h3>
        <p className="text-sm mt-0.5" style={{ color: "var(--main-text-muted)" }}>
          Complete license details with resource limits
        </p>
      </div>

      <DataTable
        columns={licenseColumns}
        rows={licenses}
        loading={loading}
        skeletonRows={3}
        getRowKey={(l) => l._id}
        emptyState={
          <EmptyState
            compact
            title="No Trinetra licenses yet"
            description="Generate one from the License Generator to see it here."
          />
        }
      />

      {/* PAGINATION */}
      {totalPages > 1 && (
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium" style={{ color: "var(--main-text-muted)" }}>
              Page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-3 py-1 rounded-lg text-sm font-medium"
                style={{
                  background: page === 1 ? "var(--card-bg)" : "var(--brand-light)",
                  color: page === 1 ? "var(--main-text-muted)" : "var(--brand)",
                  border: "1px solid var(--card-border)",
                }}
              >
                Previous
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className="px-3 py-1 rounded-lg text-sm font-medium"
                style={{
                  background: page === totalPages ? "var(--card-bg)" : "var(--brand)",
                  color: page === totalPages ? "var(--main-text-muted)" : "#ffffff",
                  border: "1px solid var(--card-border)",
                }}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TrinetraStatCard = ({ title, items }) => (
  <div className="rounded-lg p-5" style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)" }}>
    <h3 className="text-lg font-semibold mb-4" style={{ color: "var(--main-text)" }}>{title}</h3>
    <div className="space-y-1 text-md font-medium">
      {items.map((item, i) => (
        <React.Fragment key={item.label}>
          {i === 1 && <div className="border-b" style={{ borderColor: "var(--card-border)" }} />}
          <div className="flex justify-between">
            <span style={{ color: "var(--main-text-muted)" }}>{item.label}</span>
            <span className={`font-semibold ${item.color}`}>{item.value ?? 0}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  </div>
);

export default TrinetraDashboardSection;
